import React, { useEffect, useState } from "react";

import {
  formatFractionDisplay,
  formatFractionInput,
  queryIsLoading,
  scrollPage,
  useGetCarbonProgramByFarmId,
  useGetFields,
  useGetFootprintingCropFootprint,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticePlantProtections,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useTranslation,
  useUserContext,
} from "utils";

import { FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES, PRACTICE_SLUG } from "models";

import { Card, ErrorBoundary, ListCard, Message as message } from "components";

import { CropFormFlow } from "pages/app/footprinting/components//formFlows/CropFormFlow";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";

interface FootprintingCropReviewProps {
  cropId: string;
  year?: number;
}

export const FootprintingCropReview = ({ cropId, year }: FootprintingCropReviewProps) => {
  const { t } = useTranslation(["footprinting", "practices"]);
  const { user } = useUserContext();
  const farmId = user?.farm.id ?? undefined;
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const allCropsQuery = useGetPracticeCrops({ enabled: true });
  const [showFormFlow, setShowFormFlow] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>();
  const [loading, setLoading] = useState<boolean>();

  const cropFootprintQuery = useGetFootprintingCropFootprint(farmId, cropId, year);
  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const plantProtectionPracticesQuery = useGetPracticePlantProtections();
  const fieldsQuery = useGetFields();

  const handleExit = () => {
    setShowFormFlow(false);
    setActiveStep(undefined);
    message({ content: t("progressSaved"), icon: "tick" });
  };

  useEffect(() => {
    if (Number.isInteger(activeStep)) {
      setShowFormFlow(true);
    }
  }, [activeStep]);

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`radioAnswer.${isActive}`));

  useEffect(() => {
    scrollPage();
  }, [cropId]);

  useEffect(() => {
    setLoading(
      [
        cropFootprintQuery,
        carbonProgramQuery,
        allCropsQuery,
        tillagePracticesQuery,
        seedingPracticesQuery,
        mineralFertilizerPracticesQuery,
        organicFertilizerPracticesQuery,
        fieldsQuery,
      ].some(queryIsLoading)
    );
  }, [
    cropFootprintQuery,
    carbonProgramQuery,
    allCropsQuery,
    tillagePracticesQuery,
    seedingPracticesQuery,
    mineralFertilizerPracticesQuery,
    organicFertilizerPracticesQuery,
    fieldsQuery,
  ]);

  return (
    <>
      <Card className="padding-125">
        <ReviewPageTitle
          title={t("review.crop.title", { cropName: allCropsQuery.data?.find((c) => c.id === cropId)?.name })}
          iconName="klim/crops-matured"
          isLoading={loading}
        />
        {/* Crop general form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.general")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(0) }}
          dataPairs={[
            { label: t("cropGeneralForm.question.totalWeightTonsPerHa"), value: `${cropFootprintQuery.data?.footprint?.totalWeightTonsPerHa} t/ha` },
            {
              label: t("cropGeneralForm.question.preDryingMoistureContentPct"),
              value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.preDryingMoistureContentPct ?? 0)} %`,
            },
            { label: t("cropGeneralForm.question.byproductPct"), value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.byproductPct ?? 0)} %` },
            { label: t("cropGeneralForm.question.seedWeightKgPerHa"), value: `${cropFootprintQuery.data?.footprint?.seedWeightKgPerHa} kg/ha` },
            { label: t("cropGeneralForm.question.soilPh"), value: `${cropFootprintQuery.data?.footprint?.soilPh} pH` },
          ]}
        />
        {/* Crop fields form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.fields")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(1) }}
          dataPairs={cropFootprintQuery.data?.footprint?.fieldIds.map((fieldId) => {
            const field = fieldsQuery.data?.find((f) => f.id === fieldId);

            return { label: field?.name ?? "", value: `${field?.size ?? 0} ha` };
          })}
        />
        {/* Crop tillage and seeding form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.tillageAndSeeding")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(2) }}
          dataPairs={[
            {
              label: t("cropTillageSeedingForm.tillage.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.tillageActive ?? null),
              children: cropFootprintQuery.data?.footprint?.tillageActive
                ? cropFootprintQuery.data?.footprint?.tillageUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropTillageSeedingForm.tillage.count") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropTillageSeedingForm.tillage.type"),
                          value: t(`${PRACTICE_SLUG.TILLAGES}.${tillagePracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                            ns: "practices",
                          }),
                        },
                        {
                          label: t("cropTillageSeedingForm.tillage.depth"),
                          value: `${i.tillageDepth} cm`,
                        },
                        {
                          label: t("cropTillageSeedingForm.question.implementedOn"),
                          value: `${i.implementedOnHa} ha`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
            {
              label: t("cropTillageSeedingForm.seeding.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.seedingActive ?? null),
              children: cropFootprintQuery.data?.footprint?.seedingActive
                ? cropFootprintQuery.data?.footprint?.seedingUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropTillageSeedingForm.seeding.count") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropTillageSeedingForm.seeding.type"),
                          value: t(`${PRACTICE_SLUG.SEEDINGS}.${seedingPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                            ns: "practices",
                          }),
                        },
                        {
                          label: t("cropTillageSeedingForm.question.implementedOn"),
                          value: `${i.implementedOnHa} ha`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Crop fertilizers form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.fertilizers")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(3) }}
          dataPairs={[
            {
              label: t("cropFertilizerForm.synthetic.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.mineralFertilizerActive ?? null),
              children: cropFootprintQuery.data?.footprint?.mineralFertilizerActive
                ? cropFootprintQuery.data?.footprint?.mineralFertilizerUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropFertilizerForm.application.title") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropFertilizerForm.application.productName"),
                          value: t(
                            `${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${mineralFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                              ?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropFertilizerForm.application.amount"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
            {
              label: t("cropFertilizerForm.organic.question"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.organicFertilizerActive ?? null),
              children: cropFootprintQuery.data?.footprint?.organicFertilizerActive
                ? cropFootprintQuery.data?.footprint?.organicFertilizerUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropFertilizerForm.application.title") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("cropFertilizerForm.application.productName"),
                          value: t(
                            `${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${organicFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                              ?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropFertilizerForm.application.nitrogen"),
                          value: `${i.nValue} %`,
                        },
                        {
                          label: t("cropFertilizerForm.application.amount"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
            { label: t("cropFertilizerForm.application.lime"), value: `${cropFootprintQuery.data?.footprint?.limeT} tonnes` },
          ]}
        />
        {/* Crop plant protection form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.plantProtection")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(4) }}
          dataPairs={[
            {
              label: t("cropPlantProtectionForm.question.plantProtectionQuestionLabel"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.plantProtectionActive ?? null),
              children: cropFootprintQuery.data?.footprint?.plantProtectionActive
                ? cropFootprintQuery.data?.footprint?.plantProtectionUsage.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}
                      dataPairs={[
                        {
                          label: t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel"),
                          value: t(
                            `${PRACTICE_SLUG.PLANT_PROTECTIONS}.${plantProtectionPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`,
                            { ns: "practices" }
                          ),
                        },
                        {
                          label: t("cropPlantProtectionForm.question.amountAppliedQuestionLabel"),
                          value: `${i.amount} ${i.amountUnit}`,
                        },
                        {
                          label: t("cropPlantProtectionForm.question.activeIngredientsQuestionLabel"),
                          value: `${formatFractionDisplay(i.activeIngredientsPct ?? 0)} %`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Crop irrigation form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.irrigation")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(5) }}
          dataPairs={[
            {
              label: t("cropIrrigation.question.irrigation"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.irrigationActive ?? null),
            },
            ...(cropFootprintQuery.data?.footprint?.irrigationActive
              ? [
                  {
                    label: t("cropIrrigation.question.waterUsedQuestionLabel"),
                    value: `${cropFootprintQuery.data?.footprint?.waterUsedM3} m³`,
                  },
                ]
              : []),
          ]}
        />
        {/* Crop drying form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.drying")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(6) }}
          dataPairs={[
            {
              label: t("cropDryingForm.question.dryingQuestionLabel"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.dryingActive ?? null),
            },
            ...(cropFootprintQuery.data?.footprint?.dryingActive
              ? [
                  {
                    label: t("cropDryingForm.question.dryingTypeQuestionLabel"),
                    value: t(`cropDryingForm.dryingTypeOptions.${cropFootprintQuery.data?.footprint?.energyType}`),
                  },
                  {
                    label: t("cropDryingForm.question.fuelConsumptionQuestionLabel"),
                    value: `${cropFootprintQuery.data?.footprint?.fuelAmount} ${
                      cropFootprintQuery.data?.footprint?.energyType
                        ? FARM_FOOTPRINTING_DRYING_ENERGY_TYPE_SUFFIXES[cropFootprintQuery.data.footprint.energyType] || ""
                        : ""
                    }`,
                  },
                  {
                    label: t("cropDryingForm.question.renewableEnergyQuestionLabel"),
                    value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.renewableEnergyPct)} %`,
                  },
                  {
                    label: t("cropDryingForm.question.moistureContentQuestionLabel"),
                    value: `${formatFractionInput(cropFootprintQuery.data?.footprint?.postDryingMoistureContentPct)} %`,
                  },
                  {
                    label: t("cropDryingForm.question.totalWeightQuestionLabel"),
                    value: `${cropFootprintQuery.data?.footprint?.driedWeightTonsPerHa} t/ha`,
                  },
                ]
              : []),
          ]}
        />
        {/* Crop transport form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.crop.transport")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(7) }}
          dataPairs={[
            {
              label: t("cropTransportForm.question.radioQuestion"),
              value: displayRadioAnswer(cropFootprintQuery.data?.footprint?.cropSaleActive ?? null),
              children: cropFootprintQuery.data?.footprint?.cropSaleActive
                ? cropFootprintQuery.data?.footprint?.cropSales.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("farmTransportForm.retailer", { count: index + 1 })}
                      dataPairs={[
                        {
                          label: t("cropTransportForm.question.purchaserNameQuestionLabel"),
                          value: i.purchaserName ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.countryQuestionLabel"),
                          value: i.purchaserCountryCode ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.postcodeQuestionLabel"),
                          value: i.purchaserPostCode ?? "",
                        },
                        {
                          label: t("cropTransportForm.question.shareQuestionLabel"),
                          value: `${formatFractionInput(i.soldYieldPct)} %`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
      </Card>
      <ErrorBoundary>
        <CropFormFlow
          readOnly={cropFootprintQuery.data?.footprint?.isSubmitted}
          showFormFlow={showFormFlow}
          initialStep={activeStep}
          onExit={handleExit}
          cropId={cropId ?? ""}
          farmId={farmId ?? ""}
          year={year ?? 0}
          editMode
        />
      </ErrorBoundary>
    </>
  );
};
